<template>
  <div class="unit-index-container">
    <!-- Toastification loading content export -->
    <toastification-loading-content ref="loadingToast" />

    <wizard
      v-if="showFilters"
      :is-wizard-active.sync="isWizardActive"
      :item-edit.sync="itemEdit"
      :is-add.sync="isAdd"
      @refetch-data="refetchData"
      @close-wizard="closeWizard"
      :category-options="categoryOptions"
      :unit-deactivation-catalog-options="unitDeactivationCatalogOptions"
      :tenant-options="tenantOptions"
      :region-options="regionOptions"
      :country-options="countryOptions"
      :municipality-options="municipalityOptions"
      :sector-options="sectorOptions"
      :center-options="centerOptions"
      :timezone-options="timezoneOptions"
      size="xl"
    />

    <b-row v-if="showFilters" class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal
      @action="buildFilters()"
      @clear="clearSearchFilters()"
      v-if="showFilters"
    >
      <template v-slot:main-filters>
        <filters
          :category-filter.sync="categoryFilter"
          :category-options="categoryOptions"
          :tenant-filter.sync="tenantFilter"
          :tenant-options="tenantOptions"
          :online-filter.sync="onlineFilter"
          :send-c5-cdmx-filter.sync="sendC5CdmxFilter"
          :send-c5-jal-filter.sync="sendC5JalFilter"
          :municipality-options="municipalityOptions"
          :municipality-filter.sync="municipalityFilter"
          :country-options="countryOptions"
          :country-filter.sync="countryFilter"
          :region-options="regionOptions"
          :region-filter.sync="regionFilter"
          :enabled-nearby-locations-filter.sync="enabledNearbyLocationsFilter"
          :distance-filter.sync="distanceFilter"
        />
      </template>
    </filter-modal>

    <Transition name="slide-right-left">
      <div v-if="!showFilters && isFilterPanelOpen" class="filters-panel-fullscreen">
        <div class="filters-panel-header">
          <p class="h4 text-capitalize mb-2 text-white">
            {{ $t("messages.info.advanced_search") }}
          </p>
          <b-button
            variant="link"
            size="sm"
            @click="isFilterPanelOpen = false"
            class="text-dark p-0"
          >
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
        <div class="filters-panel-content">
          <filters
            :category-filter.sync="categoryFilter"
            :category-options="categoryOptions"
            :tenant-filter.sync="tenantFilter"
            :tenant-options="tenantOptions"
            :online-filter.sync="onlineFilter"
            :send-c5-cdmx-filter.sync="sendC5CdmxFilter"
            :send-c5-jal-filter.sync="sendC5JalFilter"
            :municipality-options="municipalityOptions"
            :municipality-filter.sync="municipalityFilter"
            :country-options="countryOptions"
            :country-filter.sync="countryFilter"
            :region-options="regionOptions"
            :region-filter.sync="regionFilter"
            :enabled-nearby-locations-filter.sync="enabledNearbyLocationsFilter"
            :distance-filter.sync="distanceFilter"
          />
        </div>
        <div class="filters-panel-footer">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="clearSearchFilters()"
            class="text-capitalize"
          >
            {{ $t("messages.info.clear_filters") }}
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="applyFilters()"
            class="text-capitalize"
          >
            {{ $t("search") }}
            <feather-icon icon="SearchIcon" />
          </b-button>
        </div>
      </div>
    </Transition>

    <!-- Table Container Card -->
    <b-card-group
      v-show="showFilters || !isFilterPanelOpen"
      deck
      :class="{ 'mx-2': showFilters, 'm-0': !showFilters }"
    >
      <Transition name="slide-left-right">
        <b-card class="m-0" no-body v-if="!hiddenTableGrid">
          <div class="m-2">
            <!-- Table Top -->
            <b-row>
              <!-- Per Page -->
              <b-col
                cols="12"
                md="4"
                class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
                v-if="showFilters"
              >
                <label class="text-capitalize">{{ $t("table.show") }}</label>
                <v-select
                  data-cy="automatest-v-select-pagination-control"
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                />
                <label class="text-capitalize">{{ $t("table.entries") }}</label>
              </b-col>

              <b-col cols="12" :md="showFilters ? '8' : '12'">
                <div
                  class="d-flex align-items-center justify-content-end unit-search-actions"
                  :class="{ 'w-100': !showFilters }"
                >
                  <FacetedSearch
                    :options="searchOptions"
                    :default-field="searchDefaultField"
                    @search="setFacetedFilters"
                    class='mr-1 unit-search-input'
                  />
                  <!-- <b-form-input
                    data-cy="search"
                    v-model="searchQuery"
                    :class="
                      showFilters ? 'd-inline-block mr-1 unit-search-input' : 'd-inline-block mr-1 w-75'
                    "
                    :placeholder="$t('table.search') + '...'"
                  /> -->

                  <!-- Filter button for slot mode -->
                  <b-button-group v-if="!showFilters">
                    <b-button
                      @click="isFilterPanelOpen = true"
                      class="buttonFilter cursor-pointer d-flex align-items-center"
                      data-cy="filter-btn"
                    >
                      <b-icon-filter-circle-fill scale="1.1" />
                      <span class="align-middle ml-1 text-capitalize">{{
                        $t("filters")
                      }}</span>
                    </b-button>
                    <toggle-clear-filter
                      @clear-search-filters="clearSearchFilters"
                      :filters-count="filtersCount"
                    />
                  </b-button-group>

                  <b-button
                    variant="secondary"
                    @click="openAdd()"
                    v-if="$can('create', 'Units') && showFilters"
                  >
                    <span class="text-nowrap text-capitalize">
                      {{ $t("actions.add") + " " + $tc("unit.title", 1) }}
                    </span>
                  </b-button>

                  <bulk-edit-button
                    v-if="$can('update', 'Units') && showFilters"
                    :is-bulk-edit-mode="isBulkEditMode"
                    :selected-count="bulkSelectedCount"
                    :has-selection="bulkHasSelection"
                    :select-all-matching="bulkSelectAllMatching"
                    @enable-multiselect="startBulkMultiSelect"
                    @enable-select-all="startBulkSelectAll"
                    @open-bulk-modal="openBulkModal"
                    @disable-mode="disableBulkEditMode"
                  />

                  <b-dropdown
                    v-if="($can('facilities_import_rems', 'Units') || $can('facilities_import', 'Units')) && showFilters"
                    :text="$t('messages.import.title')"
                    class="ml-1"
                    variant="info"
                    right
                    v-b-tooltip.hover
                    :title="$t('messages.import.title')"
                  >
                    <template #button-content>
                      <feather-icon icon="UploadCloudIcon" size="15" />
                    </template>

                    <b-dropdown-item  v-b-modal.import_rems_facilities_file v-if="$can('facilities_import_rems', 'Units')">
                      {{ $t('messages.import.rems') }}
                    </b-dropdown-item>
                    <b-dropdown-item v-b-modal.import_file v-if="$can('facilities_import', 'Units')">
                      {{ $t('messages.import.title') }}
                    </b-dropdown-item>
                  </b-dropdown>
                  <b-button
                    @click="exportExcel()"
                    variant="success"
                    class="text-capitalize ml-1"
                    :class="{ 'px-2': !showFilters }"
                    type="button"
                    :disabled="buttonExport"
                    v-b-tooltip.hover
                    :title="$t('messages.export.title')"
                  >
                    <feather-icon icon="DownloadCloudIcon" size="15" />
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Modal Import -->
          <div v-if="$can('facilities_import', 'Units')">
            <b-modal
              id="import_file"
              :title="`${$t('messages.import.title')}`"
              no-close-on-esc
              no-close-on-backdrop
              ref="modalImport"
            >
              <vue-dropzone
                id="dropzone_file"
                ref="dropzoneFile"
                :options="dropzoneOptions"
                class="dropzone-file"
                @vdropzone-file-added="addFile"
                @vdropzone-removed-file="removeFile"
              />
              <div v-if="importMessage.length > 0" class="mt-2 scroll">
                <span v-for="(message, index) in importMessage" :key="index">
                  <p
                    class="text-danger mb-0"
                    v-for="(error, index) in message"
                    :key="index"
                  >
                    {{ error }}
                  </p>
                </span>
              </div>
              <template #modal-footer>
                <a :href="import_example_url" download>{{
                  $t("messages.import.example_file")
                }}</a>
                <b-button
                  variant="secondary"
                  @click="$bvModal.hide('import_file')"
                  :disabled="buttonExport"
                >
                  {{ $t("actions.cancel") }}
                </b-button>
                <b-button
                  variant="primary"
                  @click="importExcel"
                  :disabled="buttonExport"
                >
                  {{ $t("actions.accept") }}
                </b-button>
              </template>
            </b-modal>
          </div>

          <!-- Modal Import REMS Facilities -->
          <div v-if="$can('facilities_import_rems', 'Units')">
            <b-modal
              id="import_rems_facilities_file"
              :title="`${$t('messages.import.title')}`"
              no-close-on-esc
              no-close-on-backdrop
              ref="modalImportRems"
            >
              <b-form-group
                label="Tenant"
                label-for="id_tenant_rems"
              >
                <v-select
                  v-model="idTenantRems"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="tenantOptions"
                  :clearable="false"
                  :reduce="(val) => val.value"
                  input-id="id_tenant_rems"
                />
                <div v-if="errorIdTenantRems" class="text-danger">
                  {{ errorIdTenantRems }}
                </div>
              </b-form-group>
              <vue-dropzone
                id="import_rems_facilities_file"
                ref="dropzoneFile"
                :options="dropzoneOptions"
                class="dropzone-file"
                @vdropzone-file-added="addFacilitieRemsFile"
                @vdropzone-removed-file="removeFacilitieRemsFile"
              />

              <template #modal-footer>
                <a :href="import_rems_example_url" download>
                  {{  $t("messages.import.example_file")}}
                </a>
                <b-button
                  variant="secondary"
                  @click="$bvModal.hide('import_rems_facilities_file')"
                  :disabled="buttonExport"
                >
                  {{ $t("actions.cancel") }}
                </b-button>
                <b-button
                  variant="primary"
                  :disabled="buttonExport"
                  @click.prevent="importFacilitiesRemsExcel"
                >
                  {{ $t("actions.accept") }}
                </b-button>
              </template>
            </b-modal>
          </div>

          <assign-task-modal
            modal-id="assign-task-modal"
            :allow-both-types="true"
            :unit-id="selectedUnitForTask ? selectedUnitForTask.id : null"
            :unit-options="unitOptionsForAssignTask"
            :form-answer-options="formTemplateOptionsForAssignTask"
            :user-options="userOptions"
            :fetch-users="fetchUsers"
            @task-assigned="handleTaskAssigned"
            @refetch-data="refetchData"
          />

          <b-table
            ref="refListTable"
            class="position-relative"
            :items="fetchList"
            responsive
            :fields="computedTableColumns"
            primary-key="id"
            :sort-by.sync="sortBy"
            show-empty
            :empty-text="$t('no_record_found')"
            :sort-desc.sync="isSortDirDesc"
            @row-selected="onRowSelected"
            select-mode="single"
            :selectable="!isBulkEditMode"
          >
            <!-- Column: bulkSelect (header) -->
            <template #head(bulkSelect)>
              <b-form-checkbox
                :checked="allVisibleSelected || bulkSelectAllMatching"
                @change="toggleSelectAllVisible"
                :indeterminate="someVisibleSelected && !allVisibleSelected && !bulkSelectAllMatching"
              />
            </template>

            <!-- Column: bulkSelect (cell) -->
            <template #cell(bulkSelect)="data">
              <b-form-checkbox
                :checked="isSelected(data.item.id)"
                @change="toggleRowSelection(data.item.id)"
                @click.native.stop
              />
            </template>

            <template #head()="data">
              <span> {{ $tc(data.label, 1) }}</span>
            </template>

            <template #head(actions)="data">
              <span> {{ $tc(data.label, 2) }}</span>
            </template>

            <!-- Column: description -->
            <template #cell(description)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <div
                    :style="`background: ${data.item.router && data.item.router.active ? 'green' : 'red'};
                        width: 1.5rem;
                        height: 1.5rem;
                        border-radius: 1rem;
                        text-align: center;`"
                  ></div>
                </template>
                <strong>{{ data.item.description }}</strong>
                <br />
                <small>{{ data.item.router ? data.item.router.tenant.name : "-" }}</small>
              </b-media>
            </template>

            <!-- Column: deactivation -->
            <template #cell(deactivation)="data">
              <feather-icon
                  data-cy="deactivation-true"
                  :icon="data.item.deactivation != null ? 'XCircleIcon': 'CheckIcon'"
                  :stroke="data.item.deactivation != null ? 'red': 'green'"
                  size="24"
                  class="align-middle text-body"
                />
            </template>

            <!-- Column: rems -->
            <template #cell(rems)="data">
              <b-media vertical-align="center">
                <strong v-if="data.item.sirh">{{ $t('unit.sirh') + ":" + data.item.sirh }}</strong>
                <br />
                <strong v-if="data.item.rems">{{ $t('unit.rems') + ":" +data.item.rems }}</strong>
                <br />
                <strong v-if="data.item.id_seproban">{{ $t('unit.id_seproban') + ":" + data.item.id_seproban }}</strong>
              </b-media>
            </template>

            <!-- Column: is_main_unit -->
            <template #cell(is_main_unit)="data">
              {{ data.item.is_main_unit ? 'P' : '' }}
            </template>

            <!-- Column: Actions -->
            <template #cell(actions)="data">
              <b-button
                :id="`actions-${data.item.id}`"
                tabindex="0"
                variant="white"
                class="px-1 btn-y"
              >
                <feather-icon
                  data-cy="actions"
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </b-button>
              <b-popover
                v-if="showFilters"
                :target="`actions-${data.item.id}`"
                triggers="focus"
                placement="bottom"
                custom-class="popover-p-0"
                variant="white"
              >
                <b-list-group class="m-0">
                  <b-list-group-item
                    @click="openEdit(data.item)"
                    v-if="$can('update', 'Units')"
                    class="border-0"
                    button
                  >
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50">{{ $t("actions.edit") }}</span>
                  </b-list-group-item>
                  <b-list-group-item
                    @click="confirmDelete(data.item.id)"
                    v-if="$can('delete', 'Units')"
                    class="border-0"
                    button
                  >
                    <feather-icon icon="TrashIcon" />
                    <span class="align-middle ml-50">{{ $t("actions.delete") }}</span>
                  </b-list-group-item>
                  <b-list-group-item
                    @click="openAssignTaskModal(data.item)"
                    v-if="$can('update', 'Units')"
                    class="border-0"
                    button
                  >
                    <feather-icon icon="CheckSquareIcon" />
                    <span class="align-middle ml-50">{{ $t("unit.assign_task") }}</span>
                  </b-list-group-item>
                  <b-list-group-item
                    v-if="$can('update', 'Units')"
                    class="border-0"
                    :to="{ name: 'plane', params: { id: data.item.id } }"
                  >
                    <feather-icon icon="MapIcon" />
                    <span class="align-middle ml-50"> planos </span>
                  </b-list-group-item>
                </b-list-group>
              </b-popover>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted text-capitalize">{{
                  $t("table.showing", {
                    from: dataMeta.from,
                    to: dataMeta.to,
                    of: dataMeta.of,
                  })
                }}</span>
              </b-col>
              <!-- Pagination -->
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <Pagination
                  data-cy="pagination"
                  :totalList="totalList"
                  :currentPage="currentPage"
                  @updatePagination="update"
                  :perPage="perPage"
                >
                </Pagination>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </Transition>

      <!-- Card details of the unit - Normal mode (side by side) -->
      <Transition name="slide-right-left" v-if="showFilters">
        <b-card
          class="mr-0 sticky-card ml-2"
          v-if="selectedUnit || loadingSelected"
          no-body
        >
          <feather-icon
            icon="XIcon"
            class="mr-2 ml-auto mt-1"
            style="cursor: pointer; position: absolute; right: 0px; z-index: 11"
            size="30"
            @click="closeInfoCard()"
          />

          <!-- UnitData Component -->
          <info-slot
            v-if="selectedUnit"
            :data="selectedUnit"
            :key="selectedUnitKey"
            class="slot-component"
          />
          <b-spinner
            v-else
            label="Spinning"
            variant="primary"
            class="spinner"
          />
        </b-card>
      </Transition>
    </b-card-group>


    <!-- Bulk Edit Wizard Modal (mirrors the unit wizard steps) -->
    <bulk-edit-wizard-modal
      :is-open="isBulkModalOpen"
      :fields="bulkEditFields"
      :form-data="bulkFormData"
      :selected-count="bulkSelectedCount"
      :is-submitting="isBulkSubmitting"
      :catalog-options="bulkCatalogOptions"
      @close="closeBulkModal"
      @submit="submitBulkEdit"
    />

    <div v-if="selectedUnit && !showFilters" class="slot-component unit-slot-detail">
      <div class="unit-slot-detail-header">
        <FacetedSearch
          :options="searchOptions"
          :default-field="searchDefaultField"
          @search="openFirstUnitMatch"
          class="unit-slot-detail-search"
        />

        <div class="unit-slot-detail-actions">
          <b-button
            size="sm"
            class="px-50"
            :variant="showDetailSearch ? 'primary' : 'outline-primary'"
            :pressed="showDetailSearch"
            v-b-tooltip.hover
            :title="$t('unit.toggle_detail_search')"
            @click="showDetailSearch = !showDetailSearch"
          >
            <feather-icon icon="TypeIcon" size="14" />
          </b-button>

          <b-button variant="link" size="sm" class="p-0" @click="closeInfoCard()">
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
      </div>
      <info-slot
        :data="selectedUnit"
        :key="selectedUnitKey"
        :show-matches-search="showDetailSearch"
        class="unit-slot-detail-info"
      />
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { ref, onMounted, watch, nextTick,computed } from "@vue/composition-api";
import useList from "./useList";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationRemsLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import store from "@/store";
import Filters from "./Filters.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import Pagination from "@/components/Pagination.vue";
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";
import ToggleClearFilter from "@/components/Filters/ToggleClearFilter.vue";
import Wizard from "./wizard/Wizard";
import UnitData from "@/views/components/GridView/layouts/slots/InfoSlot/UnitData.vue";
import axios from "@axios";
import AssignTaskModal from "./AssignTaskModal.vue";
import InfoSlot from "../components/GridView/layouts/slots/InfoSlot";

import BulkEditButton from "@/components/BulkEdit/BulkEditButton.vue";
import BulkEditWizardModal from "./BulkEditWizardModal.vue";
import useBulkEdit from "@/composables/useBulkEdit";
import {
  BULK_EDIT_FIELDS,
  BULK_EDIT_ADDITIONAL_INFO_KEY,
  BULK_EDIT_EMERGENCY_CONTACT_KEY,
  BULK_EDIT_ENDPOINT,
  BULK_EDIT_BATCH_STATUS_ENDPOINT,
  BULK_EDIT_IDS_KEY,
} from "./bulkEditConfig";
import FacetedSearch from "@/components/FacetedSearch.vue";
import {
  UNIT_SEARCH_OPTIONS,
  UNIT_SEARCH_DEFAULT_FIELD,
  fetchFirstUnitMatch,
} from "@/utils/unitSearch";

export default {
  components: {
    BreadcrumbMenu,
    ButtonFilters,
    ToggleClearFilter,
    FilterModal,
    Filters,
    Pagination,
    vSelect,
    ToastificationLoadingContent,
    ToastificationRemsLoadingContent,
    vueDropzone: vue2Dropzone,
    Wizard,
    UnitData,
    AssignTaskModal,
    InfoSlot,
    BulkEditButton,
    BulkEditWizardModal,
    FacetedSearch
  },
  props: {
    filters: {
      type: Object | Array,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, context) {
    const searchOptions = UNIT_SEARCH_OPTIONS;
    const searchDefaultField = UNIT_SEARCH_DEFAULT_FIELD;

    const isSidebarActiveByChatbot = store.getters["chatbot/getIsSidebarActive"];
    const isFilterPanelOpen = ref(false);
    // Slot mode: the in-detail searcher can be collapsed to give the open
    // unit the whole slot. It lives here so the state survives the info slot
    // remount that opening each unit forces.
    const showDetailSearch = ref(true);
    const hiddenTableGrid = ref(false);
    const selectedUnit = ref(null);
    const loadingSelected = ref(false);
    const selectedUnitKey = ref(0);
    const selectedUnitForTask = ref(null);

    const unitOptionsForAssignTask = computed(() => {
      const tableItems = refListTable.value?.localItems ?? [];
      const options = tableItems.map((item) => ({
        value: item.id,
        label: item.description,
      }));
      if (selectedUnitForTask.value?.id && !options.some((opt) => opt.value === selectedUnitForTask.value.id)) {
        options.unshift({
          value: selectedUnitForTask.value.id,
          label: selectedUnitForTask.value.description,
        });
      }
      return options;
    });

    const update = (data) => {
      currentPage.value = data;
    };

    const action = ref("list");
    const exportExcel = () => {
      action.value == "export";
      buttonExport.value = true;
      loadingToast.value.show(
        trans["messages.export.download"],
        trans["messages.export.Generating_file"],
        "secondary"
      );
      store
        .dispatch("unit/export", {
          q: searchQuery.value,
          max: perPage.value,
          "page[n]": currentPage.value,
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          tenant: tenantFilter.value,
          country: countryFilter.value,
          region: regionFilter.value,
          municipality: municipalityFilter.value,
          online: onlineFilter.value,
          send_c5_cdmx: sendC5CdmxFilter.value,
          send_c5_jal: sendC5JalFilter.value,
          action: "export",
          category: categoryFilter.value,
          near_location: enabledNearbyLocationsFilter.value,
          distance: distanceFilter.value,
        })
        .then((response) => {
          console.log(response);
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: trans["messages.error.server_error"],
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          loadingToast.value.close();
          buttonExport.value = false;
        });
    };

    const isSidebarActive = ref(isSidebarActiveByChatbot);
    const itemEdit = ref({});
    const isAdd = ref(false);
    const isWizardActive = ref(false);
    const modalImport = ref();
    const modalImportRems = ref();
    const loadingToast = ref(null);
    const toastImportRems = ref(null);
    const idTenantRems = ref(null);
    const errorIdTenantRems = ref(null);

    const openEdit = async (item) => {
      itemEdit.value = item;
      itemEdit.value.hasRouter = 1;

      // additional routers
      const additional_routers = (item.additional_routers || []).map(item => ({
        ...item,
        hasRouter: 1,
      }));

      itemEdit.value = {
        ...item,
        additional_routers,
        router: {
          ...item.router,
          deactivation: {
            ...item.router?.deactivation,
            comment: item.router?.deactivation?.comment ?? "",
            reactivation_date: item.router?.deactivation?.reactivation_date ?? null,
          },
        },
        isUnitDisabled: item.router?.deactivation != null ? true : false,
      };
      isAdd.value = false;
      await nextTick();
      isWizardActive.value = true;
    };

    const clearEditParam = () => {
      const q = { ...(context.root.$route.query || {}) };
      if ("editUnitId" in q) {
        delete q.editUnitId;
        context.root.$router.replace({ query: q });
      }
    };

    const closeWizard = () => {
      isWizardActive.value = false;
      isAdd.value = false;
      clearEditParam();
    };

    const toast = useToast();

    const { t } = useI18nUtils();
    const buttonExport = ref(false);
    const trans = {
      "messages.import.upload": t("messages.import.upload"),
      "messages.import.uploading_file": t("messages.import.uploading_file"),
      "messages.import.upload_succesfully": t("messages.import.upload_succesfully"),
      "messages.export.download": t("messages.export.download"),
      "messages.export.Generating_file": t("messages.export.Generating_file"),
      "messages.error.server_error": t("messages.error.server_error"),
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "no_record_found": t("no_record_found"),
    };

    const {
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      formatDateAssigned,
      categoryOptions,
      categoryFilter,
      unitDeactivationCatalogOptions,
      tenantOptions,
      tenantFilter,
      onlineFilter,
      countryFilter,
      countryOptions,
      regionFilter,
      regionOptions,
      municipalityOptions,
      municipalityFilter,
      sendC5CdmxFilter,
      sendC5JalFilter,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      sectorOptions,
      centerOptions,
      userOptions,
      timezoneOptions,
      enabledNearbyLocationsFilter,
      distanceFilter,
      fetchUsers,
      formTemplateOptionsForAssignTask,
      setFacetedFilters,
    } = useList(props.showFilters, props.filters);

    const buildActiveFilters = () => {
      const candidates = {
        q: searchQuery.value,
        tenant: tenantFilter.value,
        country: countryFilter.value,
        region: regionFilter.value,
        municipality: municipalityFilter.value,
        online: onlineFilter.value,
        send_c5_cdmx: sendC5CdmxFilter.value,
        send_c5_jal: sendC5JalFilter.value,
        category: categoryFilter.value,
        near_location: enabledNearbyLocationsFilter.value,
        distance: distanceFilter.value,
      };
      const active = {};
      Object.entries(candidates).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          active[key] = value;
        }
      });
      return active;
    };

    const bulkEdit = useBulkEdit({
      endpoint: BULK_EDIT_ENDPOINT,
      // The list steps have no plain input: the wizard owns their UI, this
      // only seeds their formData keys (null = untouched).
      fields: [
        ...BULK_EDIT_FIELDS,
        { key: BULK_EDIT_ADDITIONAL_INFO_KEY },
        { key: BULK_EDIT_EMERGENCY_CONTACT_KEY },
      ],
      idsKey: BULK_EDIT_IDS_KEY,
      batchStatusEndpoint: BULK_EDIT_BATCH_STATUS_ENDPOINT,
      getSelectAllExtras: buildActiveFilters,
      onSuccess: () => {
        refetchData();
      },
    });

    watch(
      totalList,
      (n) => {
        bulkEdit.totalCount.value = n || 0;
      },
      { immediate: true }
    );

    // Teams catalog: feeds the section select on the emergency contacts step
    // (same source the unit wizard uses).
    store.dispatch("teams/getAll");
    const teamsOptions = computed(() => store.getters["teams/getSelectedItems"]);

    // Bulk selects that depend on another field (country → state →
    // municipality), fetched apart so the shared store catalogs the list
    // filters use stay unfiltered.
    const useBulkDependentOptions = (fetchOptions) => {
      const options = ref([]);
      let latestRequestId = 0;

      const clear = () => {
        latestRequestId += 1;
        options.value = [];
      };

      const load = async (parentValue) => {
        const requestId = ++latestRequestId;

        try {
          const { data } = await fetchOptions(parentValue);

          // A newer parent was selected while this request was in flight.
          if (requestId !== latestRequestId) return;

          options.value = (data?.data || []).map((record) => ({
            value: record.id,
            label: record.name,
          }));
        } catch (error) {
          if (requestId !== latestRequestId) return;

          options.value = [];
          toast({
            component: ToastificationContent,
            props: {
              title: trans["messages.error.server_error"],
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        }
      };

      return { options, load, clear };
    };

    const bulkRegions = useBulkDependentOptions((idCountry) =>
      axios.get("v1/region", {
        params: { id_country: [idCountry], max: 1000 },
      })
    );

    const bulkMunicipalities = useBulkDependentOptions((idRegion) =>
      axios.get("v1/municipality", {
        params: { region: [idRegion], max: 1000 },
      })
    );

    // Changing a parent resets its child (nulling id_region also chains into
    // the municipality watcher), so no value of the old parent is submitted.
    watch(
      () => bulkEdit.formData.value.id_country,
      (idCountry) => {
        if (bulkEdit.formData.value.id_region) {
          bulkEdit.formData.value.id_region = null;
        }

        if (!idCountry) {
          bulkRegions.clear();
          return;
        }

        bulkRegions.load(idCountry);
      }
    );

    watch(
      () => bulkEdit.formData.value.id_region,
      (idRegion) => {
        if (bulkEdit.formData.value.id_municipality) {
          bulkEdit.formData.value.id_municipality = null;
        }

        if (!idRegion) {
          bulkMunicipalities.clear();
          return;
        }

        bulkMunicipalities.load(idRegion);
      }
    );

    const isSaturdayOptions = [{ value: '1', label: "Sí" }, { value: '0', label: "No" }];

    const bulkCatalogOptions = computed(() => ({
      teamsOptions: teamsOptions.value,
      categoryOptions: categoryOptions.value,
      tenantOptions: tenantOptions.value,
      centerOptions: centerOptions.value,
      sectorOptions: sectorOptions.value,
      countryOptions: countryOptions.value,
      regionOptions: bulkRegions.options.value,
      municipalityOptions: bulkMunicipalities.options.value,
      timezoneOptions: timezoneOptions.value,
      isSaturdayOptions,
    }));

    const computedTableColumns = computed(() => {
      if (bulkEdit.isBulkEditMode.value) {
        return [
          { key: "bulkSelect", label: "", sortable: false, thStyle: "width: 40px" },
          ...tableColumns,
        ];
      }
      return tableColumns;
    });

    const startBulkMultiSelect = () => {
      bulkEdit.clearSelection();
      bulkEdit.enableBulkEditMode();
    };

    const startBulkSelectAll = () => {
      bulkEdit.enableBulkEditMode();
      bulkEdit.enableSelectAllMatching();
    };

    const visibleItems = computed(() => {
      return refListTable.value?.localItems || [];
    });

    const allVisibleSelected = computed(() => {
      const visible = visibleItems.value;
      if (!visible.length) return false;
      return visible.every(item => bulkEdit.isSelected(item.id));
    });

    const someVisibleSelected = computed(() => {
      const visible = visibleItems.value;
      if (!visible.length) return false;
      return visible.some(item => bulkEdit.isSelected(item.id));
    });

    const toggleSelectAllVisible = () => {
      if (bulkEdit.selectAllMatching.value) {
        bulkEdit.clearSelection();
        return;
      }
      const visible = visibleItems.value;
      if (allVisibleSelected.value) {
        visible.forEach(item => {
          if (bulkEdit.isSelected(item.id)) {
            bulkEdit.toggleItem(item.id);
          }
        });
      } else {
        visible.forEach(item => {
          if (!bulkEdit.isSelected(item.id)) {
            bulkEdit.toggleItem(item.id);
          }
        });
      }
    };

    // Checkbox de fila. Si estamos en "seleccionar todo" y desmarcan una fila,
    // salimos de ese modo conservando el resto de filas visibles seleccionadas.
    const toggleRowSelection = (id) => {
      if (bulkEdit.selectAllMatching.value) {
        const remaining = visibleItems.value
          .map(item => item.id)
          .filter(itemId => itemId !== id);
        bulkEdit.setSelectedIds(remaining);
        return;
      }
      bulkEdit.toggleItem(id);
    };

    const confirmDelete = function (id) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("unit/delete", id)
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: response.message,
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                      timeout: 10000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: response.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                    timeout: 10000,
                }
              );
            });
        }
      });
    };

    const openAdd = () => {
      isAdd.value = true;
      isWizardActive.value = true;
    };

    const applyFilters = () => {
      buildFilters();
      isFilterPanelOpen.value = false;
    };

    const onRowSelected = (item) => {
      const [unitData] = item;

      selectedUnit.value = null;

      if (!item.length) {
        loadingSelected.value = false;
        return;
      }

      loadingSelected.value = true;

      if (!_.showFilters) {
        hiddenTableGrid.value = true;
      }

      // Incrementar la key para forzar el re-renderizado del componente
      selectedUnit.value = {
        data:{
          ...unitData,
          emergency_contacts: unitData.emergency_contact,
          in_app_source: 'subsystem_tree',
          //id: null,
        }
      }

      selectedUnitKey.value += 1;
      loadingSelected.value = false;
    };

    // Slot mode: the faceted search above the info slot opens the first unit
    // matching the filters. Same sort and relations as the table fetch, so the
    // opened unit is the first row the main view would show for that search.
    const openFirstUnitMatch = async (filters) => {
      if (!filters || !Object.keys(filters).length) return;

      try {
        const unit = await fetchFirstUnitMatch(filters, {
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
        });

        if (!unit) {
          toast({
            component: ToastificationContent,
            props: {
              title: trans["no_record_found"],
              icon: "AlertTriangleIcon",
              variant: "warning",
            },
          });
          return;
        }

        onRowSelected([unit]);
      } catch (e) {
        console.error(e);
        toast({
          component: ToastificationContent,
          props: {
            title: trans["messages.error.server_error"],
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      }
    };

    const closeInfoCard = () => {
      selectedUnit.value = null;
      loadingSelected.value = false;
      hiddenTableGrid.value = false;
    };

    const openAssignTaskModal = (unitItem) => {
      selectedUnitForTask.value = unitItem;
      context.root.$bvModal.show("assign-task-modal");
    };

    const handleTaskAssigned = () => {
      context.root.$bvModal.hide("assign-task-modal");
      refetchData();
    };

    const dropzoneOptions = ref({
      url: "https://httpbin.org/post",
      paramName: "file",
      autoProcessQueue: false,
      addRemoveLinks: true,
      dictRemoveFile: t("actions.delete"),
      acceptedFiles: ".xlsx,.xls",
      maxFilesize: 10,
      dictDefaultMessage: t("messages.import.select_file") + " (.xlsx .xls)",
    });

    const dropzoneFile = ref(null);
    const importMessage = ref([]);

    const dropzoneFacilitiesRemsFile = ref(null);
    const importFacilitiesRemsMessages = ref([]);

    const addFile = (file) => {
      importMessage.value = [];
      dropzoneFile.value = file;
      if (file.size > 10000000) {
        dropzoneFile.value = null;
      }
    };

    const addFacilitieRemsFile = (file) => {
      importFacilitiesRemsMessages.value = [];
      dropzoneFacilitiesRemsFile.value   = file;
    };

    const removeFile = () => {
      dropzoneFile.value = null;
    };

    const removeFacilitieRemsFile = () => {
      dropzoneFacilitiesRemsFile.value = null;
    };

    const importExcel = () => {
      if (dropzoneFile.value == null) return;

      loadingToast.value.show(
        trans["messages.import.upload"],
        trans["messages.import.uploading_file"],
        "secondary"
      );
      buttonExport.value = true;

      let formData = new FormData();
      formData.append("file", dropzoneFile.value);
      formData.append("action", "import");

      store
        .dispatch("unit/add", formData)
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            modalImport.value.hide();
          }
        })
        .catch((response) => {
          importMessage.value = response.response.data.data;
          loadingToast.value.close();
          toast({
            component: ToastificationContent,
            props: {
              title: `${response.response.data.message}`,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          buttonExport.value = false;
          loadingToast.value = false;
        });
    };

    const importFacilitiesRemsExcel = async () => {
      errorIdTenantRems.value = null;

      if (!idTenantRems.value) {
        errorIdTenantRems.value = 'El campo tenant es requerido';
        return;
      }

      if (dropzoneFacilitiesRemsFile.value == null) return;

      const headers = {
        headers: { "Content-Type": 'multipart/form-data' }
      }

      const formData = new FormData();

      formData.append("file", dropzoneFacilitiesRemsFile.value);
      formData.append("id_tenant", idTenantRems.value);

      buttonExport.value = true;

      await axios.post('v1/unit/import-rems', formData, headers)
        .then((response) => {
          modalImportRems.value.hide();

          toastImportRems.value = toast({
            component: ToastificationContent,
            props: {
              title: 'La importación de instalaciones se ha iniciado. El proceso puede tardar unos minutos.',
              icon: 'InfoIcon',
              variant: 'primary'
            }
          },{
            timeout: false
          });

          dropzoneFacilitiesRemsFile.value = null;
          errorIdTenantRems.value = null;
          idTenantRems.value = null;

          modalImportRems.value.hide();
        })
        .catch((error) => {
          toast.clear(toastImportRems.value);

          toast({
            component: ToastificationContent,
            props: {
              title: 'Error al iniciar la importación',
              icon: "AlertTriangleIcon",
              variant: "danger",
            }
          });
        })
        .finally(() => {
          buttonExport.value = false;
        });
    };

    const applySearchFromQuery = async () => {
      const q = context.root.$route.query || {};
      if (q.search && q.search !== searchQuery.value) {
        searchQuery.value = q.search;
      }
      await nextTick();
      buildFilters();
    };

    const loadAndOpenFromQuery = async () => {
      await applySearchFromQuery();

      const id = context.root.$route.query && context.root.$route.query.editUnitId;
      if (!id) return;

      let item = null;

      try {
        const res = await store.dispatch("unit/show", id);
        item = (res && (res.data?.data || res.data || res)) || null;
      } catch (e) {}

      if (!item || !item.id) {
        try {
          const res = await axios.get(`v1/unit/${id}`, { validateStatus: s => s < 500 });
          if (res.status >= 200 && res.status < 300) item = res.data?.data || null;
        } catch (e) {}
      }

      if (item && item.id) {
        await openEdit(item);
      }
    };

    const connectSocket = () => {
      window.Echo.private(`import-rems-units-completed-user-${user.value.id}`)
        .listen('.import-units-rems-completed', () => setImportRemsUnitsNotification());

      window.Echo.private(`import-rems-units-failed-user-${user.value.id}`)
        .listen('.import-units-rems-failed', (event) => setImportRemsUnitsFailedNotification(event));
    }

    const setImportRemsUnitsNotification = () => {
      if (toastImportRems.value) {
        toast.clear(toastImportRems.value);
      }

      toast({
        component: ToastificationContent,
        props: {
          title: 'Importación de instalaciones finalizada correctamente',
          icon: 'CheckIcon',
          variant: 'success',
        },
      });

      store.dispatch('facility_category/getAll');
      refetchData();
    }

    const setImportRemsUnitsFailedNotification = (event) => {
      const { message,rems } = event;

      toast({
        component: ToastificationContent,
        props: {
          title: `${message} \n REMS: ${rems}`,
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      },{
        timeout: false
      });
    }

    const user = computed(() => store.getters["user/getUser"]);

    onMounted(async () => {
      connectSocket();

      await fetchUsers();
      await loadAndOpenFromQuery();
    });

    watch(
      () => context.root.$route.query && context.root.$route.query.search,
      async (n, o) => {
        if (n !== o) {
          await applySearchFromQuery();
        }
      }
    );

    watch(
      () => context.root.$route.query && context.root.$route.query.editUnitId,
      async (n, o) => {
        if (n && n !== o) {
          await loadAndOpenFromQuery();
        }
      }
    );

    return {
      searchOptions,
      searchDefaultField,
      action,
      exportExcel,
      update,
      isSidebarActive,
      isWizardActive,
      closeWizard,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      fetchList,
      tableColumns,
      perPage,
      currentPage,
      totalList,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      categoryOptions,
      categoryFilter,
      unitDeactivationCatalogOptions,
      tenantOptions,
      tenantFilter,
      onlineFilter,
      countryFilter,
      countryOptions,
      regionOptions,
      regionFilter,
      municipalityOptions,
      municipalityFilter,
      sendC5CdmxFilter,
      sendC5JalFilter,
      enabledNearbyLocationsFilter,
      distanceFilter,
      buttonExport,
      importExcel,
      dropzoneOptions,
      addFile,
      removeFile,
      importMessage,
      loadingToast,
      toastImportRems,
      modalImport,
      modalImportRems,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      import_example_url: `${window.__env.VUE_APP_BASE_URL}/import_examples/import_facilities.xlsx`,
      import_rems_example_url: `${window.__env.VUE_APP_BASE_URL}/import_examples/import_rems_facilities.xlsx`,
      sectorOptions,
      centerOptions,
      isFilterPanelOpen,
      applyFilters,
      hiddenTableGrid,
      showDetailSearch,
      selectedUnit,
      selectedUnitKey,
      loadingSelected,
      onRowSelected,
      closeInfoCard,
      selectedUnitForTask,
      unitOptionsForAssignTask,
      formTemplateOptionsForAssignTask,
      openAssignTaskModal,
      handleTaskAssigned,
      userOptions,
      openFirstUnitMatch,
      dropzoneFacilitiesRemsFile,
      importFacilitiesRemsMessages,
      addFacilitieRemsFile,
      removeFacilitieRemsFile,
      importFacilitiesRemsExcel,
      idTenantRems,
      errorIdTenantRems,
      fetchUsers,
      timezoneOptions,

      // Bulk Edit
      isBulkEditMode: bulkEdit.isBulkEditMode,
      bulkSelectedCount: bulkEdit.selectedCount,
      bulkHasSelection: bulkEdit.hasSelection,
      bulkSelectAllMatching: bulkEdit.selectAllMatching,
      toggleBulkEditMode: bulkEdit.toggleBulkEditMode,
      disableBulkEditMode: bulkEdit.disableBulkEditMode,
      startBulkMultiSelect,
      startBulkSelectAll,
      openBulkModal: bulkEdit.openBulkModal,
      closeBulkModal: bulkEdit.closeBulkModal,
      submitBulkEdit: bulkEdit.submitBulkEdit,
      isBulkModalOpen: bulkEdit.isBulkModalOpen,
      bulkEditFields: BULK_EDIT_FIELDS,
      bulkFormData: bulkEdit.formData,
      isBulkSubmitting: bulkEdit.isSubmitting,
      bulkCatalogOptions,
      computedTableColumns,
      toggleItem: bulkEdit.toggleItem,
      toggleRowSelection,
      isSelected: bulkEdit.isSelected,
      allVisibleSelected,
      someVisibleSelected,
      toggleSelectAllVisible,
      setFacetedFilters
    };
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/utilities/transitions";

.unit-index-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.unit-search-actions {
  flex-wrap: wrap;
  row-gap: 0.5rem;
}

.unit-search-input {
  min-width: 160px;
  flex: 1 1 0;
}

.router {
  max-width: 500px;
}

.dropzone-file {
  padding: 0px;
  margin: auto;
  border: none;
  max-width: 250px;
  min-height: 200px;
  display: flex;
  box-shadow: inset 0 0 0 3px #dfdfdf;
}

.dropzone {
  .dz-preview {
    margin: 0px;
    width: 100%;
  }

  .dz-message {
    text-align: center;
    margin: auto;
  }
}

.scroll {
  width: 100%;
  height: 100px;
  overflow-x: hidden;
  overflow-y: auto;
}

.scroll::-webkit-scrollbar {
  height: 5px;
  width: 10px;
}

.scroll::-webkit-scrollbar-thumb {
  background: #1b3d5e;
}

.filters-panel-fullscreen {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 0;
}

.filters-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.filters-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}

.filters-panel-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-shrink: 0;
}

.unit-detail-fullscreen {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 0;
}

.unit-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.unit-detail-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
}

.sticky-card {
  position: sticky;
  top: 6rem;
  height: 80vh;
  overflow: auto;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.spinner {
  margin-top: calc(50% - 100px);
  margin-left: 50%;
}

.showScroll {
  position: sticky;
  top: 6rem;
  height: auto;
  overflow: hidden;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark-layout {
  .filters-panel-fullscreen {
    background-color: #283046;
    color: #b4b7bd;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .filters-panel-header {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  .filters-panel-footer {
    border-top-color: #3b4253;
  }

  .filters-panel-content {
    &::-webkit-scrollbar-track {
      background: #161d31;
    }

    &::-webkit-scrollbar-thumb {
      background: #3b4253;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #4b5563;
    }
  }

  .unit-detail-fullscreen {
    background-color: #283046;
    color: #b4b7bd;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .unit-detail-header {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  // Same background as the info slot below it, so the header reads as part
  // of the detail instead of a white band over the dark theme.
  .unit-slot-detail-header {
    background-color: var(--third-dark);
    border-bottom-color: rgba(255, 255, 255, 0.06);
  }
}

.slot-component {
  width: 100%;
  height: 100%;
}

// Slot mode detail: fixed search header on top, the info slot scrolls below
// it, so its own sticky matches-input sticks under this header instead of
// fighting it for the top of the outer scroller.
.unit-slot-detail {
  display: flex;
  flex-direction: column;
}

.unit-slot-detail-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  flex-shrink: 0;
  background-color: var(--main-light);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.unit-slot-detail-search {
  flex: 1 1 0;
  min-width: 0;
}

.unit-slot-detail-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
}

.unit-slot-detail-info {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
}
</style>
